import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import defaultProfile from "../../../assets-user/agents/default_avatar.png";
import api from "../../../configs/api";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import {
  confirmAction,
  toastError,
  toastSuccess,
} from "../../../utils/confirmAction";
import { formatDate } from "../../../utils/helpers";
import ApprovalCard from "./ApprovalCard";
import ApprovalCardSkeleton from "./ApprovalCardSkeleton";

const OwnerDetailsModal = ({ owner, onClose }) => {
  const modalRef = React.useRef(null);

  useOutsideClick(modalRef, onClose, !!owner);
  if (!owner) return null;

  const property = owner.postedProperties?.[0];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-900"
        >
          ✕
        </button>

        <div className="flex gap-4 mb-6">
          <img
            src={owner.profileImage || defaultProfile}
            className="w-20 h-20 rounded-full object-cover border"
            alt=""
          />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {owner.fullName}
            </h2>
            <p className="text-sm text-text-muted">{owner.email}</p>
            <p className="text-sm text-text-muted">{owner.phone}</p>
            <p className="text-sm mt-1 text-status-info">
              City: {owner.city || "—"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-text-secondary">
              Total Properties Posted
            </p>
            <p>{owner.postedPropertiesCount}</p>
          </div>

          <div>
            <p className="font-medium text-text-secondary">Latest Posted On</p>
            <p>{formatDate(owner.lastPropertyDate)}</p>
          </div>

          <div className="col-span-2">
            <p className="font-medium text-text-secondary mb-1">
              Latest Property
            </p>
            <div className="flex gap-3 items-center">
              <img
                src={property?.thumbnail || "https://placehold.co/80"}
                className="w-20 h-16 rounded-lg object-cover"
                alt=""
              />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm cursor-pointer">
                    {property?.title}
                  </p>
                  <p className="text-xs text-text-muted">{property?.type}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const OwnerApprovalList = ({ list, loading }) => {
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(list.filter((o) => o.fullName.toLowerCase().includes(q)));
  }, [search, list]);

  const handleApprove = async (owner) => {
    const confirm = await confirmAction(
      "Approve Owner?",
      `This will verify ${owner.fullName}'s profile.`,
      "Approve"
    );
    if (!confirm.isConfirmed) return;

    try {
      await api.post(`/approvals/owners/approve/${owner.id}`);
      toastSuccess("Owner approved!");
      setFiltered((prev) => prev.filter((o) => o.id !== owner.id));
    } catch (err) {
      console.error(err);
      toastError("Approval failed");
    }
  };

  const handleReject = async (owner) => {
    const confirm = await confirmAction(
      "Reject Owner?",
      `This will reject ${owner.fullName}'s profile.`,
      "Reject"
    );
    if (!confirm.isConfirmed) return;

    try {
      await api.post(`/approvals/owners/reject/${owner.id}`);
      toastSuccess("Owner rejected.");
      setFiltered((prev) => prev.filter((o) => o.id !== owner.id));
    } catch (err) {
      console.error(err);
      toastError("Rejection failed");
    }
  };

  return (
    <div className="p-4">
      {/* Search */}
      <input
        type="text"
        placeholder="Search Owner"
        className="border border-border px-4 py-2 rounded-lg mb-4 w-full focus:outline-primary"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Skeleton */}
      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <ApprovalCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Results */}
      {!loading && filtered.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((owner) => (
            <ApprovalCard
              key={owner.id}
              item={owner}
              onSelect={() => setSelected(owner)}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <p className="text-center text-text-muted mt-10">
          No pending owners found.
        </p>
      )}

      {/* Modal */}
      <OwnerDetailsModal owner={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default OwnerApprovalList;
